import React, { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { HiOutlineSearch } from 'react-icons/hi';
import { FaApple, FaGooglePlay, FaGlobe, FaDesktop } from 'react-icons/fa';
import { portfolioProjects, projectStatusMeta, projectCategories } from '../data/portfolioProjects';
import ProjectCard from '../components/portfolio/ProjectCard';
import ProjectDetailModal from '../components/portfolio/ProjectDetailModal';

const statusOrder = ['all', 'live', 'beta', 'development', 'archived', 'discontinued'];

const platformIconMap = {
  web: FaGlobe,
  ios: FaApple,
  android: FaGooglePlay,
  desktop: FaDesktop,
};

const PortfolioPage = () => {
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('all');
  const [category, setCategory] = useState('All');
  const [platformFilter, setPlatformFilter] = useState([]);
  const [selected, setSelected] = useState(null);

  const togglePlatform = (p) => {
    setPlatformFilter((prev) => (prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]));
  };

  const stats = useMemo(() => {
    const counts = {
      total: portfolioProjects.length,
      live: 0,
      beta: 0,
      development: 0,
      archived: 0,
      discontinued: 0,
    };
    portfolioProjects.forEach((p) => {
      if (counts[p.status] !== undefined) counts[p.status] += 1;
    });
    return counts;
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return portfolioProjects.filter((p) => {
      if (status !== 'all' && p.status !== status) return false;
      if (category !== 'All' && p.category !== category) return false;
      if (platformFilter.length > 0 && !platformFilter.every((pf) => p.platforms?.includes(pf)))
        return false;
      if (!q) return true;
      const hay = [p.name, p.tagline, p.description, p.client, ...(p.techStack || [])]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [query, status, category, platformFilter]);

  return (
    <>
      <Helmet>
        <title>Project Command Center — HexaPi Tech</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="min-h-screen bg-primary-50">
        {/* HERO STRIP */}
        <header className="relative overflow-hidden bg-gradient-to-br from-neutral-dark via-primary-700 to-accent-700 text-white">
          {/* Subtle hex pattern overlay */}
          <div className="hero-grid-pattern-dark pointer-events-none absolute inset-0 opacity-90" />

          {/* Soft color glows — tinted, not white, so the gradient stays rich */}
          <div className="pointer-events-none absolute -right-32 -top-32 h-[28rem] w-[28rem] rounded-full bg-accent-500/30 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-40 -left-20 h-[26rem] w-[26rem] rounded-full bg-primary-400/25 blur-3xl" />
          <div className="pointer-events-none absolute left-1/2 top-1/3 h-40 w-[40rem] -translate-x-1/2 rounded-full bg-accent-400/10 blur-3xl" />

          {/* Hexagon motifs */}
          <svg
            className="absolute right-10 top-6 hidden h-36 w-36 animate-float-slow text-white/25 md:block"
            viewBox="0 0 100 100"
            fill="none"
            aria-hidden
          >
            <polygon
              points="50,5 93,25 93,75 50,95 7,75 7,25"
              stroke="currentColor"
              strokeWidth="1.5"
            />
          </svg>
          <svg
            className="absolute bottom-8 right-1/4 hidden h-20 w-20 animate-float text-accent-300/40 lg:block"
            viewBox="0 0 100 100"
            fill="none"
            aria-hidden
            style={{ animationDelay: '1.5s' }}
          >
            <polygon
              points="50,10 85,30 85,70 50,90 15,70 15,30"
              stroke="currentColor"
              strokeWidth="2"
            />
          </svg>
          {/* Floating dots for depth */}
          <span className="absolute left-[12%] top-1/4 hidden h-2 w-2 animate-float-slow rounded-full bg-accent-300/60 md:block" />
          <span
            className="absolute bottom-1/4 left-1/3 h-1.5 w-1.5 animate-float rounded-full bg-white/50"
            style={{ animationDelay: '2s' }}
          />

          <div className="relative mx-auto max-w-6xl px-5 py-9 sm:py-12 lg:px-8 lg:py-16">
            <div className="animate-stagger-2 flex animate-slide-in-left flex-wrap items-center gap-3 opacity-0">
              <img
                src="/images/logo/logo.png"
                alt="HexaPi Tech"
                className="h-9 w-auto drop-shadow-lg sm:h-10 md:h-12"
              />
              <h1 className="text-2xl font-bold leading-tight tracking-tight sm:text-3xl md:text-5xl">
                Project Command Center
              </h1>
            </div>
            <p className="animate-stagger-3 mt-3 max-w-xl animate-slide-in-left text-sm text-white/85 opacity-0 sm:text-base">
              Every HexaPi Tech project in one place
            </p>

            {/* Stats row */}
            <div className="animate-stagger-4 mt-6 grid animate-slide-up grid-cols-3 gap-2 opacity-0 sm:mt-7 sm:grid-cols-5 sm:gap-3">
              <StatTile label="Projects" value={stats.total} accent />
              <StatTile label="Live" value={stats.live} dot="bg-emerald-400" />
              <StatTile label="Beta" value={stats.beta} dot="bg-amber-300" />
              <StatTile label="In Dev" value={stats.development} dot="bg-sky-300" />
              <StatTile
                label="Archived"
                value={stats.archived + stats.discontinued}
                dot="bg-slate-300"
              />
            </div>
          </div>
        </header>

        {/* CONTROL BAR */}
        <div className="sticky top-0 z-30 border-b border-primary-100 bg-primary-50/90 backdrop-blur-md">
          <div className="mx-auto max-w-6xl space-y-2.5 px-5 py-3 sm:space-y-3 lg:px-8 lg:py-4">
            {/* Search + platform toggles */}
            <div className="flex flex-col gap-2.5 sm:gap-3 md:flex-row">
              <div className="relative flex-1">
                <HiOutlineSearch className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral" />
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search by name, stack, client…"
                  className="w-full rounded-lg bg-white py-2.5 pl-9 pr-3 text-base text-neutral-dark outline-none ring-1 ring-primary-100 transition-all focus:ring-2 focus:ring-primary-400 sm:text-sm"
                />
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <span className="hidden text-[11px] font-semibold uppercase tracking-wider text-neutral md:inline">
                  Platform
                </span>
                <div className="flex items-center gap-1">
                  {Object.entries(platformIconMap).map(([p, Icon]) => {
                    const active = platformFilter.includes(p);
                    return (
                      <button
                        key={p}
                        onClick={() => togglePlatform(p)}
                        title={p}
                        aria-label={`Filter by ${p}`}
                        className={`inline-flex h-10 w-10 items-center justify-center rounded-lg transition-all sm:h-9 sm:w-9 ${
                          active
                            ? 'bg-primary-500 text-white shadow-card'
                            : 'bg-white text-neutral-dark ring-1 ring-primary-100 hover:ring-primary-300'
                        }`}
                      >
                        <Icon className="h-4 w-4" />
                      </button>
                    );
                  })}
                </div>

                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="ml-auto rounded-lg bg-white px-3 py-2.5 text-base text-neutral-dark outline-none ring-1 ring-primary-100 focus:ring-2 focus:ring-primary-400 sm:text-sm md:ml-0"
                >
                  {projectCategories.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Status pills — horizontal scroll on very small screens to avoid awkward wrap */}
            <div className="no-scrollbar -mx-5 flex items-center gap-1.5 overflow-x-auto px-5 sm:mx-0 sm:flex-wrap sm:px-0">
              {statusOrder.map((s) => {
                const label =
                  s === 'all'
                    ? `All (${stats.total})`
                    : `${projectStatusMeta[s].label} (${stats[s] || 0})`;
                const active = status === s;
                return (
                  <button
                    key={s}
                    onClick={() => setStatus(s)}
                    className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-semibold transition-all ${
                      active
                        ? 'bg-primary-500 text-white shadow-card'
                        : 'bg-white text-neutral-dark ring-1 ring-primary-100 hover:ring-primary-300'
                    }`}
                  >
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* GRID */}
        <main className="mx-auto max-w-6xl px-5 py-6 sm:py-8 lg:px-8 lg:py-10">
          {filtered.length === 0 ? (
            <div className="py-20 text-center">
              <p className="text-base text-neutral">No projects match your filters.</p>
              <button
                onClick={() => {
                  setQuery('');
                  setStatus('all');
                  setCategory('All');
                  setPlatformFilter([]);
                }}
                className="mt-4 inline-flex items-center rounded-lg bg-primary-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-600"
              >
                Reset filters
              </button>
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 sm:gap-5 lg:grid-cols-3 xl:grid-cols-4">
              {filtered.map((p, i) => (
                <ProjectCard key={p.id} project={p} index={i} onOpen={setSelected} />
              ))}
            </div>
          )}
        </main>

        {selected && <ProjectDetailModal project={selected} onClose={() => setSelected(null)} />}
      </div>
    </>
  );
};

const StatTile = ({ label, value, dot, accent }) => (
  <div
    className={`relative rounded-xl px-2.5 py-2 ring-1 backdrop-blur-md sm:px-3 sm:py-2.5 ${accent ? 'bg-white/20 ring-white/30' : 'bg-white/10 ring-white/20'}`}
  >
    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-white/80">
      {dot && <span className={`inline-block h-1.5 w-1.5 rounded-full ${dot}`} />}
      <span className="truncate">{label}</span>
    </div>
    <div className="mt-0.5 text-xl font-bold leading-tight sm:text-2xl">{value}</div>
  </div>
);

export default PortfolioPage;
